// LIFTILE product catalog — used across listing, home featured, and detail pages.
// Imagery references user-provided layouts and Unsplash construction imagery.

export const BRAND = {
  name: "LIFTILE",
  tagline: "Rise With Confidence",
  phone: "+91 8866939091",
  email: "info@liftile.com",
  address: "29, SRS Industrial Park, NH-48, Por, Vadodara, Gujarat – 391243",
  mapsUrl: "https://maps.app.goo.gl/2UXRaUVLvVDNvQKC8",
  mapsEmbedUrl:
    "https://www.google.com/maps?q=29+SRS+Industrial+Park+NH-48+Por+Vadodara+Gujarat+391243&output=embed",
  logo: "/liftile-logo-full-light.png",
  logoMark: "/liftile-logo-mark-light.png",
};

export const HERO_BG =
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=2000&q=85"; // sunset cranes

// Realistic LIFTILE project sites across India. Each entry maps an on-site
// photograph to the project metadata used on the Projects page (name,
// city/state, category, machine used) plus the combined `location` label
// used on the Home page.
export const PROJECTS = [
  {
    image: "/project-thiruvananthapuram.png",
    name: "Thiruvananthapuram",
    city: "Thiruvananthapuram",
    state: "Kerala",
    category: "High-Rise Construction",
    machine: "Passenger & Material Hoist",
    location: "Thiruvananthapuram, Kerala",
  },
  {
    image: "/project-iocl-vadodara.png",
    name: "IOCL Refinery",
    city: "Vadodara",
    state: "Gujarat",
    category: "Industrial Project",
    machine: "Conventional Tower Hoist",
    location: "IOCL Refinery • Vadodara, Gujarat",
  },
  {
    image: "/project-adani-korba.png",
    name: "Adani Power Plant",
    city: "Korba",
    state: "Chhattisgarh",
    category: "Power Plant",
    machine: "Multi Functional Tower Hoist",
    location: "Adani Power Plant • Korba, Chhattisgarh",
  },
  {
    image: "/project-kota.png",
    name: "Kota",
    city: "Kota",
    state: "Rajasthan",
    category: "Commercial Construction",
    machine: "Conventional Tower Hoist",
    location: "Kota, Rajasthan",
  },
  {
    image: "/project-alappuzha.png",
    name: "Alappuzha",
    city: "Alappuzha",
    state: "Kerala",
    category: "Residential Construction",
    machine: "Passenger & Material Hoist",
    location: "Alappuzha, Kerala",
  },
  {
    image: "/project-pandesara-surat.png",
    name: "Pandesara",
    city: "Pandesara, Surat",
    state: "Gujarat",
    category: "Industrial Project",
    machine: "Multi Functional Tower Hoist",
    location: "Pandesara, Surat, Gujarat",
  },
];

// Backward-compatibility alias (only image URLs).
export const PROJECT_IMAGES = PROJECTS.map((p) => p.image);

export const PRODUCTS = [
  {
    slug: "conventional-tower-hoist",
    name: "Conventional Tower Hoist",
    tagline: "Reliable. Robust. Ready.",
    category: "Material Handling",
    short_description: "Cost-effective vertical material transport for mid-rise construction.",
    image: "/product-conventional-hoist.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1597007030739-6d2e7172ee72?w=1200&q=80",
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
      "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&q=80",
    ],
    description:
      "LIFTILE's Conventional Tower Hoist is engineered for safe, efficient material lifting up to 60m. Built with high-tensile mast sections and dual-braking systems, it's the workhorse of mid-rise residential and commercial projects.",
    features: [
      { title: "Heavy Duty Gearbox", desc: "Smooth and powerful performance under continuous load." },
      { title: "Dual Safety Brake", desc: "Mechanical + electromagnetic redundancy." },
      { title: "Modular Mast Sections", desc: "Easy on-site assembly and height adjustment." },
      { title: "Low Maintenance", desc: "Engineered for long life with minimal downtime." },
    ],
    specifications: [
      { label: "Load Capacity", value: "500 kg" },
      { label: "Lifting Height", value: "Up to 60 m" },
      { label: "Lifting Speed", value: "30 m/min" },
      { label: "Motor Power", value: "5.5 kW" },
      { label: "Power Supply", value: "415V, 50Hz, 3-Phase" },
      { label: "Standard", value: "IS 3696" },
    ],
    applications: ["Residential Towers", "Mid-Rise Commercial", "Renovation Sites", "Bridge Works"],
  },
  {
    slug: "multi-functional-tower-hoist",
    name: "Multi Functional Tower Hoist",
    tagline: "One Machine. Endless Possibilities.",
    category: "Material Handling",
    short_description: "Versatile hoist for materials & light personnel transport.",
    image: "/product-multifunc-hoist.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?w=1200&q=80",
      "https://images.unsplash.com/photo-1590725140246-20acdee442be?w=1200&q=80",
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80",
    ],
    description:
      "Built for flexibility, the Multi Functional Tower Hoist seamlessly switches between material and light personnel duties — ideal for projects that need agility without compromising safety.",
    features: [
      { title: "Dual Mode Operation", desc: "Material + light personnel transport." },
      { title: "Anti-Fall Safety Device", desc: "SAJ 40-1.2 certified locking." },
      { title: "VFD Control", desc: "Jerk-free starts and stops." },
      { title: "Spacious Cage", desc: "Optimised for varied payloads." },
    ],
    specifications: [
      { label: "Load Capacity", value: "800 kg" },
      { label: "Lifting Height", value: "Up to 100 m" },
      { label: "Lifting Speed", value: "0–40 m/min" },
      { label: "Motor Power", value: "2 × 7.5 kW" },
      { label: "Power Supply", value: "415V, 50Hz, 3-Phase" },
      { label: "Standard", value: "IS 3696 / EN 12158" },
    ],
    applications: ["High-Rise Buildings", "Infrastructure", "Industrial Plants", "Metro Projects"],
  },
  {
    slug: "passenger-cum-material-hoist",
    name: "Passenger Cum Material Hoist",
    tagline: "Safe Lifting. Maximum Height.",
    category: "Material Handling",
    short_description: "Twin-cage hoist for safe transport of personnel and materials up to 150m.",
    image: "/product-passenger-hoist.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1590725140246-20acdee442be?w=1200&q=80",
      "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&q=80",
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
    ],
    description:
      "LIFTILE Passenger Cum Material Hoist is engineered for high performance, offering safe and reliable vertical transportation of both materials and personnel at construction sites. Built with robust components and advanced safety systems.",
    features: [
      { title: "Robust Structure", desc: "Heavy-duty steel for high durability and stability." },
      { title: "Safety First", desc: "Overload protection, safety brakes & anti-fall devices." },
      { title: "Smooth Operation", desc: "Advanced control for jerk-free vertical movement." },
      { title: "Easy Installation", desc: "Modular design for quick installation and maintenance." },
      { title: "Versatile Use", desc: "Ideal for high-rise and industrial projects." },
    ],
    specifications: [
      { label: "Model", value: "LIFT1000 / LIFT1500 / LIFT2000" },
      { label: "Load Capacity", value: "1000 / 1500 / 2000 kg" },
      { label: "Lifting Height", value: "Up to 150 m" },
      { label: "Lifting Speed", value: "0–60 m/min (Variable)" },
      { label: "Motor Power", value: "2 × 11 kW" },
      { label: "Power Supply", value: "415V, 50Hz, 3-Phase" },
      { label: "Safety Device", value: "SAJ 40-1.2 or Equivalent" },
      { label: "Braking System", value: "Dual (Electromagnetic + Mechanical)" },
      { label: "Mast Section", value: "650 × 650 × 1508 mm" },
      { label: "Cage Size", value: "1.3m × 2.0m × 2.2m (Customizable)" },
      { label: "Standard", value: "IS 3696 / EN 12158" },
    ],
    applications: ["High-Rise Buildings", "Infrastructure Projects", "Commercial Complexes", "Residential Projects", "Industrial Projects", "Maintenance Work"],
  },
  {
    slug: "rope-suspended-platform",
    name: "Suspended Platform",
    tagline: "Reliable Access. Any Height.",
    category: "Access Equipment",
    short_description: "Modular suspended platform for facade work, painting & maintenance.",
    image: "/product-suspended-platform.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1531834685032-c34bf0d84c77?w=1200&q=80",
      "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80",
      "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=1200&q=80",
    ],
    description:
      "Modular suspended working platforms with dual wire rope safety and hot-dip galvanized construction. Perfect for facade installation, painting, glass cleaning and maintenance of high-rise buildings.",
    features: [
      { title: "Dual Wire Rope Safety", desc: "Independent primary and secondary lines." },
      { title: "Modular Platform", desc: "1.5m to 7.5m configurable lengths." },
      { title: "Anti-Fall Lock", desc: "Automatic activation on rope failure." },
      { title: "Galvanized Build", desc: "Long life in harsh weather." },
    ],
    specifications: [
      { label: "Platform Length", value: "1.5 – 7.5 m" },
      { label: "Load Capacity", value: "500 – 800 kg" },
      { label: "Lifting Speed", value: "8 – 10 m/min" },
      { label: "Motor Power", value: "1.8 kW × 2" },
      { label: "Wire Rope", value: "8.3 mm steel core" },
      { label: "Standard", value: "EN 1808" },
    ],
    applications: ["Facade Cleaning", "Glass Installation", "Painting & Plastering", "Maintenance"],
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    tagline: "Powerful Lifting. Smooth Operation.",
    category: "Cranes",
    short_description: "Compact mini jib crane for slab-level material movement.",
    image: "/product-monkey-lift.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1597007030739-6d2e7172ee72?w=1200&q=80",
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
    ],
    description:
      "Also known as Mini Jib Crane. Compact, slab-mountable lifting solution for moving small loads between floors during construction. Quick to deploy, easy to operate.",
    features: [
      { title: "Compact Footprint", desc: "Mounts on any slab edge." },
      { title: "Manual Slewing", desc: "Easy load positioning." },
      { title: "Wire Rope Drum", desc: "Smooth and precise lifting." },
      { title: "Heavy-Duty Frame", desc: "Powder-coated steel." },
    ],
    specifications: [
      { label: "Load Capacity", value: "200 – 500 kg" },
      { label: "Jib Length", value: "1.5 m" },
      { label: "Motor", value: "2 HP / 3 HP" },
      { label: "Wire Rope", value: "8 mm" },
      { label: "Power", value: "Single / Three Phase" },
    ],
    applications: ["Floor-to-Floor Lifting", "Small Material Handling", "Repair Works"],
  },
  {
    slug: "bar-bending-machine",
    name: "Bar Bending Machine",
    tagline: "Perfect Bending. Every Time.",
    category: "Rebar Equipment",
    short_description: "Precision rebar bending for bars up to 42mm.",
    image: "/product-bar-bending.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1565008576549-57569a49371d?w=1200&q=80",
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
    ],
    description:
      "Precision-engineered bar bending machine for accurate, repeatable rebar bends. Programmable angle control, hardened bending discs, and heavy-duty gearing make it perfect for high-volume rebar workshops.",
    features: [
      { title: "Programmable Angle", desc: "0°–180° precision control." },
      { title: "Hardened Discs", desc: "Long service life under high stress." },
      { title: "Foot Pedal Control", desc: "Hands-free operation." },
      { title: "Emergency Stop", desc: "Safety-first operation." },
    ],
    specifications: [
      { label: "Max Bar Diameter", value: "42 mm" },
      { label: "Bending Angle", value: "0° – 180°" },
      { label: "Motor Power", value: "3 kW" },
      { label: "Bending Speed", value: "9 rpm" },
      { label: "Weight", value: "385 kg" },
    ],
    applications: ["Rebar Workshops", "Site Bending", "Precast Plants"],
  },
  {
    slug: "bar-cutting-machine",
    name: "Bar Cutting Machine",
    tagline: "Precision Cutting. Maximum Speed.",
    category: "Rebar Equipment",
    short_description: "Heavy-duty rebar cutting for bars up to 50mm.",
    image: "/product-bar-cutting.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1565008576549-57569a49371d?w=1200&q=80",
    ],
    description:
      "Heavy-duty bar cutting machine engineered for clean, fast, and accurate cuts on high-tensile rebar. Built with shock-resistant frame and oil-lubricated drive system.",
    features: [
      { title: "Shock-Resistant Frame", desc: "Withstands continuous cutting load." },
      { title: "Oil-Lubricated Drive", desc: "Smooth operation, long life." },
      { title: "Hardened Blades", desc: "Replaceable, long-lasting cutters." },
      { title: "One-Touch Operation", desc: "Simple foot-pedal control." },
    ],
    specifications: [
      { label: "Max Bar Diameter", value: "50 mm" },
      { label: "Motor Power", value: "4 kW" },
      { label: "Cutting Speed", value: "32 cuts/min" },
      { label: "Power", value: "415V, 3-Phase" },
    ],
    applications: ["Rebar Yards", "Construction Sites", "Precast Manufacturing"],
  },
  {
    slug: "wheel-barrow",
    name: "Wheel Barrow",
    tagline: "Built Tough. Built To Last.",
    category: "Site Equipment",
    short_description: "Heavy-duty construction wheel barrow with pneumatic tyre.",
    image: "/product-wheel-barrow.jpg",
    gallery: [
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=1200&q=80",
    ],
    description:
      "Heavy-duty industrial wheel barrow with reinforced steel tray, pneumatic tyre, and powder-coated finish. Built to take the punishment of daily construction site use.",
    features: [
      { title: "Reinforced Tray", desc: "16-gauge cold-rolled steel." },
      { title: "Pneumatic Tyre", desc: "Smooth ride over rough sites." },
      { title: "Powder-Coated", desc: "Rust resistant finish." },
      { title: "Ergonomic Handles", desc: "Reduces operator fatigue." },
    ],
    specifications: [
      { label: "Tray Capacity", value: "85 litres" },
      { label: "Load Capacity", value: "150 kg" },
      { label: "Wheel", value: "Pneumatic 14\"" },
      { label: "Material", value: "16G Steel" },
    ],
    applications: ["Concrete Transport", "Material Movement", "General Site Work"],
  },
  {
    slug: "concrete-mixer",
    name: "Concrete Mixer",
    tagline: "Strong Mixing. Consistent Output.",
    category: "Concrete Equipment",
    short_description: "Heavy-duty drum mixer engineered for daily site productivity.",
    image: "/product-concrete-mixer.jpg",
    gallery: ["/product-concrete-mixer.jpg"],
    description:
      "LIFTILE Concrete Mixer delivers consistent batch mixing for residential, commercial and infrastructure projects. Heavy-duty drum, robust gear drive and dust-resistant construction make it the workhorse of every site.",
    features: [
      { title: "Heavy-Duty Drum", desc: "Hardened steel for long service life." },
      { title: "Robust Drive", desc: "Smooth and efficient gear transmission." },
      { title: "Easy Mobility", desc: "Wheel-mounted for quick relocation." },
      { title: "Diesel / Electric", desc: "Choose the power source for your site." },
    ],
    specifications: [
      { label: "Drum Capacity", value: "10/7 cu.ft (Hopper)" },
      { label: "Motor", value: "5 HP / 7.5 HP" },
      { label: "Power", value: "Single / Three Phase" },
      { label: "Mounting", value: "Wheel-mounted" },
    ],
    applications: ["Residential", "Commercial", "Infrastructure", "Precast"],
  },
  {
    slug: "reversible-drum-concrete-mixer",
    name: "Reversible Drum Concrete Mixer",
    tagline: "Higher Output. Faster Discharge.",
    category: "Concrete Equipment",
    short_description: "Reversible drum mixer with hopper-fed loading for high-volume projects.",
    image: "/product-reversible-drum-mixer.jpg",
    gallery: ["/product-reversible-drum-mixer.jpg"],
    description:
      "LIFTILE Reversible Drum Concrete Mixer (RD 8000) delivers high-volume mixing with hopper-fed loading and reverse-rotation discharge. Engineered for medium to large concrete works including high-rise, bridges and slab pours.",
    features: [
      { title: "Reverse Discharge", desc: "Fast, clean concrete output via drum reverse." },
      { title: "Hopper Loader", desc: "Mechanical hopper for one-man operation." },
      { title: "Heavy-Duty Frame", desc: "Skid-mounted, transportable design." },
      { title: "High Output", desc: "Up to 8 cu.m per hour of mixed concrete." },
    ],
    specifications: [
      { label: "Model", value: "RD 8000" },
      { label: "Output", value: "Up to 8 m³/hr" },
      { label: "Drum Capacity", value: "800/560 litres" },
      { label: "Motor", value: "15 HP" },
      { label: "Power", value: "415V, 3-Phase" },
    ],
    applications: ["High-Rise Construction", "Bridges", "Slab Pours", "Infrastructure"],
  },
];

export const PRODUCT_CATEGORIES = [
  { name: "Material Hoists", description: "Safe lifting. Maximum height.", count: 3, image: PRODUCTS[2].image },
  { name: "Rope Access Equipment", description: "Reliable access at any height.", count: 1, image: PRODUCTS[3].image },
  { name: "Cranes", description: "Powerful lifting. Smooth operation.", count: 1, image: PRODUCTS[4].image },
  { name: "Rebar Equipment", description: "Precision cutting. Perfect bending.", count: 2, image: PRODUCTS[5].image },
  { name: "Concrete Equipment", description: "Powerful mixing. Durable build.", count: 2, image: PRODUCTS[8].image },
  { name: "Site Equipment", description: "Everyday tools that last.", count: 1, image: PRODUCTS[7].image },
];

export const TESTIMONIALS = [
  { name: "Rajesh Patel", company: "Patel Constructions, Ahmedabad", quote: "LIFTILE's passenger hoist has been running flawlessly on our 32-storey project for over 18 months. Zero downtime, exceptional support.", rating: 5 },
  { name: "Suresh Kumar", company: "Skyline Infra, Mumbai", quote: "Switched from a competitor to LIFTILE for our metro project. The build quality and after-sales service are simply outstanding.", rating: 5 },
  { name: "Anita Sharma", company: "BuildRight Engineers, Delhi", quote: "The rope suspended platform paid for itself in 4 months. Worker safety has improved dramatically and our facade work is 60% faster.", rating: 5 },
  { name: "Mohammed Iqbal", company: "Falcon Builders, Hyderabad", quote: "We've standardised our entire fleet on LIFTILE equipment. Reliability, parts availability and service network — they tick every box.", rating: 5 },
];

export const CLIENTS = [
  "L&T", "Adani", "Tata Projects", "Shapoorji Pallonji", "GMR", "DLF", "Afcons", "Hiranandani", "Raheja", "Godrej",
];
